import React, { useState, useCallback, useEffect, useRef } from 'react';
import { friendlyDeleteError } from '../../utils/deleteError';
import { formatDate } from '../../utils/formatDate';
import '../../styles/components/FormPage.css';
import '../../styles/components/ProductCatalog.css';
import { Input as FormInput } from '../FormField';
import { CurrencyInput } from '../ui/CurrencyInput';
import { useMoney } from '../../hooks/useMoney';
import { useRole } from '../../hooks/useRole';
import { currencySymbol } from '../../utils/currency';
import { toMicro } from '../../utils/decimal';
import { supplierCostKey, withoutSupplierCosts, type SupplierCostKey } from '../../utils/supplierPrice';
import RichTextEditor from '../ui/RichTextEditor';
import TagMultiSelect from '../ui/TagMultiSelect';
import SearchableSelect from '../ui/SearchableSelect';
import SidebarPhotoCard from '../ui/SidebarPhotoCard';
import RecordActivity from '../ui/RecordActivity';
import Icon from '../Icon';
import { Product, ProductType, ProductSupplier, StockMovement, StockMovementReason, STOCK_MOVEMENT_REASON_LABELS } from '../../types/Product';
import { getProduct, createProduct, updateProduct, archiveProduct, recordStockMovement } from '../../services/ProductService';
import { uploadImage } from '../../services/uploadService';
import { loadVendorData } from '../../data/VendorLoader';
import { useNotifications } from '../NotificationProvider';
import { useFormSaveEvent, useFormDeleteEvent, generateId, reportSaveError, useFieldErrors } from './_shared';

const TYPE_OPTIONS: ProductType[] = ['favour', 'asoebi', 'fabric', 'decor', 'accessories', 'internal'];

interface CatalogItemFormPageProps {
  productId?: string;
  onSave: () => void;
  onCancel: () => void;
  onNameLoad?: (name: string) => void;
}

function emptySupplier(): ProductSupplier {
  return { id: generateId(), name: '', isPreferred: false };
}

function formStateFor(product: Product | undefined) {
  return {
    name: product?.name ?? '',
    description: product?.description ?? '',
    types: product?.types ?? ([] as ProductType[]),
    image: product?.image ?? '',
    customizable: product?.customizable ?? false,
    suppliers: product?.suppliers && product.suppliers.length > 0 ? product.suppliers : [emptySupplier()],
    stockPools: product?.stockPools ?? ({} as Partial<Record<ProductType, number>>),
  };
}

/** What each supplier's cost box holds, as the text typed (money is never a float here). */
function costTextFor(suppliers: ProductSupplier[], key: SupplierCostKey | undefined): Record<string, string> {
  const text: Record<string, string> = {};
  for (const s of suppliers) {
    const v = key ? s[key] : undefined;
    text[s.id] = v === null || v === undefined ? '' : String(v);
  }
  return text;
}

export const CatalogItemFormPage: React.FC<CatalogItemFormPageProps> = ({ productId, onSave, onCancel, onNameLoad }) => {
  const { showSuccess, showError } = useNotifications();
  // Supplier cost is money: a contractor or financially-blind person gets no
  // cost box and nothing cost-related in the payload. Everyone else edits the
  // cost in the project's own currency (see supplierCostKey).
  const { canViewFinancials } = useRole();
  const money = useMoney();
  const costColumn = canViewFinancials ? supplierCostKey(money.currency) : null;
  const fieldErrors = useFieldErrors();
  const existing = productId ? getProduct(productId) : undefined;

  useEffect(() => {
    if (existing?.name) onNameLoad?.(existing.name);
  }, [existing?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const [vendorOptions, setVendorOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    loadVendorData().then((vendors) => {
      setVendorOptions(vendors.map((v) => ({ value: v.name, label: v.name })));
    }).catch(() => {});
  }, []);

  const [form, setForm] = useState(() => formStateFor(existing));
  const [costText, setCostText] = useState(() => costTextFor(formStateFor(existing).suppliers, costColumn?.key));
  const [imageUploading, setImageUploading] = useState(false);
  const [movements, setMovements] = useState<StockMovement[]>(existing?.stock_movements ?? []);
  const [stockEntry, setStockEntry] = useState<Record<string, { qty: string; reason: StockMovementReason; note: string }>>({});
  const [recordingStock, setRecordingStock] = useState<string | null>(null);
  const tempIdRef = useRef<string>(existing?.id ?? crypto.randomUUID());

  // Every piece of state above is seeded once, at mount. Going from one
  // catalogue item straight to another is a client-side navigation that reuses
  // this component instance, so without this the previous item's values stay
  // on screen and a save writes them onto the new item. Keyed on the id, not
  // on the record's contents, so re-reading the same product can't wipe edits
  // the user has in progress.
  const loadedIdRef = useRef(productId);
  useEffect(() => {
    if (loadedIdRef.current === productId) return;
    loadedIdRef.current = productId;
    setForm(formStateFor(existing));
    setCostText(costTextFor(formStateFor(existing).suppliers, costColumn?.key));
    setMovements(existing?.stock_movements ?? []);
    setStockEntry({});
    setRecordingStock(null);
    tempIdRef.current = existing?.id ?? crypto.randomUUID();
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Customizable is set from the breadcrumb toggle (Header.tsx), matching the
  // fabric-published breadcrumb-toggle pattern, instead of a checkbox buried
  // mid-form.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('catalog-customizable-sync', { detail: form.customizable }));
  }, [form.customizable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onSet = (e: Event) => set('customizable', (e as CustomEvent).detail as boolean);
    window.addEventListener('catalog-customizable-set', onSet);
    return () => window.removeEventListener('catalog-customizable-set', onSet);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function set<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function setSupplier(index: number, patch: Partial<ProductSupplier>) {
    setForm((p) => ({
      ...p,
      suppliers: p.suppliers.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  }

  function addSupplier() {
    setForm((p) => ({ ...p, suppliers: [...p.suppliers, emptySupplier()] }));
  }

  function removeSupplier(index: number) {
    setForm((p) => ({ ...p, suppliers: p.suppliers.filter((_, i) => i !== index) }));
  }

  const handleImageFile = async (file: File) => {
    setImageUploading(true);
    try {
      const attachment = await uploadImage(file, {
        entityType: 'product',
        entityId: tempIdRef.current,
        label: 'cover',
      });
      set('image', attachment.url);
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setImageUploading(false);
    }
  };

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) { showError('Name is required'); return; }
    if (form.types.length === 0) { showError('Select at least one type'); return; }

    const named = form.suppliers.filter((s) => s.name.trim());
    let suppliers: ProductSupplier[] | undefined;
    if (!canViewFinancials) {
      // The API refuses any write that carries `suppliers` from a blind caller, so send none.
      suppliers = undefined;
    } else if (costColumn) {
      const bad = named.find((s) => (costText[s.id] ?? '').trim() !== '' && toMicro(costText[s.id]) === null);
      if (bad) { showError(`Enter the ${bad.name.trim()} cost as a number`); return; }
      // The API's cost columns are numbers, so the typed text is converted
      // here and only here; nothing adds or compares these as floats.
      suppliers = named.map((s) => {
        const text = (costText[s.id] ?? '').trim();
        return { ...s, [costColumn.key]: text === '' ? undefined : Number(text) };
      });
    } else {
      // No project currency yet: leave the stored costs as they are.
      suppliers = named;
    }
    const data = {
      name: form.name.trim(),
      description: form.description || undefined,
      types: form.types,
      suppliers,
      stockPools: form.stockPools,
      image: form.image || undefined,
      customizable: form.customizable,
      archived: existing?.archived ?? false,
    };

    try {
      if (existing) {
        await updateProduct(existing.id, data);
        showSuccess('Item updated');
      } else {
        await createProduct(data as Omit<Product, 'id' | 'created_at' | 'updated_at'>);
        showSuccess('Item added');
      }
    } catch (err) {
      // Stay on the form so the entered values aren't lost.
      reportSaveError(err, fieldErrors, showError, 'Failed to save item');
      return;
    }
    onSave();
  }, [form, costText, costColumn, canViewFinancials, existing, onSave, showSuccess, showError]);

  useFormSaveEvent(handleSave);

  const handleDelete = useCallback(async () => {
    if (!existing) return;
    try {
      await archiveProduct(existing.id);
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'item', action: 'archive' }));
      return;
    }
    showSuccess('Item archived');
    onCancel();
  }, [existing, onCancel, showSuccess, showError]);

  useFormDeleteEvent(handleDelete);

  const getStockEntry = (type: ProductType) => stockEntry[type] ?? { qty: '', reason: 'received' as StockMovementReason, note: '' };
  const setStockEntryField = (type: ProductType, patch: Partial<{ qty: string; reason: StockMovementReason; note: string }>) => {
    setStockEntry((p) => ({ ...p, [type]: { ...getStockEntry(type), ...patch } }));
  };

  const handleRecordStock = async (type: ProductType) => {
    if (!existing) return;
    const entry = getStockEntry(type);
    const delta = parseInt(entry.qty, 10) || 0;
    if (delta === 0) return;
    setRecordingStock(type);
    try {
      const updated = await recordStockMovement(existing.id, type, delta, entry.reason, entry.note || undefined);
      if (updated) {
        set('stockPools', updated.stockPools);
        setMovements(updated.stock_movements ?? []);
        setStockEntry((p) => ({ ...p, [type]: { qty: '', reason: 'received', note: '' } }));
      } else {
        showError('Failed to record stock movement');
      }
    } finally {
      setRecordingStock(null);
    }
  };

  return (
    <>
    <div className="detail-layout">
      <div className="inline-form-page">
        <div className="form-row">
          <FormInput label="Name" type="text" value={form.name} error={fieldErrors.errorFor('name')}
            onChange={(e) => { set('name', e.target.value); fieldErrors.clear('name'); }} placeholder="e.g. Engraved Photo Coaster" autoFocus required />
          <div className="form-group pc-types-select">
            <label>Types</label>
            <TagMultiSelect
              values={form.types}
              onChange={(vals) => set('types', vals as ProductType[])}
              options={TYPE_OPTIONS}
              placeholder="favour, fabric, asoebi..."
            />
          </div>
        </div>

        <RichTextEditor label="Description" value={form.description}
          onChange={(v) => set('description', v)} rows={3}
          placeholder="Describe this item..." />

        <div className="pc-suppliers-section">
          <div className="pc-suppliers-header-row">
            <p className="form-section-title">Suppliers</p>
            <button type="button" className="lie-add-btn pc-add-supplier-btn" onClick={addSupplier}>
              <Icon name="plus" size={12} /> Add Supplier
            </button>
          </div>
          <div className={`pc-supplier-row-header${costColumn ? '' : ' pc-supplier-row--no-cost'}`}>
            <span>Name</span>
            {costColumn && <span className="pc-supplier-col-numeric">Cost ({costColumn.currency})</span>}
            <span className="pc-supplier-col-numeric">Min Qty</span><span>Preferred</span><span />
          </div>
          {form.suppliers.map((s, i) => (
            <div key={s.id} className={`pc-supplier-row${costColumn ? '' : ' pc-supplier-row--no-cost'}`}>
              <SearchableSelect
                value={s.name}
                onChange={(v) => setSupplier(i, { name: v })}
                onCreateNew={(v) => setSupplier(i, { name: v })}
                options={vendorOptions}
                placeholder="Supplier name"
              />
              {costColumn && (
                <CurrencyInput min="0" wrapperClassName="pc-supplier-col-numeric" symbol={currencySymbol(costColumn.currency)}
                  aria-label={`Cost in ${costColumn.currency}`}
                  value={costText[s.id] ?? ''}
                  onChange={(e) => { const v = e.target.value; setCostText((p) => ({ ...p, [s.id]: v })); }} />
              )}
              <FormInput type="number" min="1" className="pc-supplier-col-numeric" value={s.minQuantity ?? ''}
                onChange={(e) => setSupplier(i, { minQuantity: e.target.value ? parseInt(e.target.value, 10) : undefined })} />
              <input type="checkbox" checked={s.isPreferred ?? false}
                onChange={(e) => setSupplier(i, { isPreferred: e.target.checked })} />
              <button type="button" className="btn-icon" title="Remove supplier" onClick={() => removeSupplier(i)}>
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>

        {form.types.length > 0 && (
          <div className="pc-stock-section">
            <hr className="poe-divider" />
            <h3 className="poe-sub-heading">Stock on hand</h3>
            {form.types.map((t) => {
              const entry = getStockEntry(t);
              const typeMovements = movements.filter((m) => m.type === t).slice().reverse();
              return (
                <div key={t} className="pc-stock-type-block">
                  {/* A single-type product already names its type in the
                      Types field above — repeating it here as a row label
                      just restated the same word. Only show the label when
                      there's more than one type to disambiguate between. */}
                  {form.types.length > 1 && (
                    <div className="stat-row pc-stock-row">
                      <span>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
                      {existing ? (
                        <strong>{form.stockPools[t] ?? 0}</strong>
                      ) : (
                        <FormInput type="number" min="0" className="pc-stock-input"
                          value={form.stockPools[t] ?? 0}
                          onChange={(e) => set('stockPools', { ...form.stockPools, [t]: parseInt(e.target.value, 10) || 0 })} />
                      )}
                    </div>
                  )}
                  {form.types.length === 1 && !existing && (
                    <div className="stat-row pc-stock-row">
                      <span>Initial stock</span>
                      <FormInput type="number" min="0" className="pc-stock-input"
                        value={form.stockPools[t] ?? 0}
                        onChange={(e) => set('stockPools', { ...form.stockPools, [t]: parseInt(e.target.value, 10) || 0 })} />
                    </div>
                  )}
                  {form.types.length === 1 && existing && (
                    <div className="stat-row pc-stock-row">
                      <span>In stock</span>
                      <strong>{form.stockPools[t] ?? 0}</strong>
                    </div>
                  )}

                  {existing && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="bv-label">Quantity</label>
                          <input
                            type="number"
                            className="form-input form-input--sm"
                            placeholder="+50 or -10"
                            value={entry.qty}
                            onChange={(e) => setStockEntryField(t, { qty: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label className="bv-label">Reason</label>
                          <SearchableSelect
                            value={entry.reason}
                            onChange={(v) => setStockEntryField(t, { reason: v as StockMovementReason })}
                            options={(Object.keys(STOCK_MOVEMENT_REASON_LABELS) as StockMovementReason[]).map((r) => ({ value: r, label: STOCK_MOVEMENT_REASON_LABELS[r] }))}
                          />
                        </div>
                        <div className="form-group">
                          <label className="bv-label">Note (optional)</label>
                          <input
                            type="text"
                            className="form-input form-input--sm"
                            placeholder="e.g. Batch from the main supplier"
                            value={entry.note}
                            onChange={(e) => setStockEntryField(t, { note: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="gat-form-actions">
                        <button
                          type="button"
                          className="btn-secondary pc-stock-record-btn"
                          disabled={!entry.qty || parseInt(entry.qty, 10) === 0 || recordingStock === t}
                          onClick={() => handleRecordStock(t)}
                        >
                          {recordingStock === t ? 'Recording…' : 'Record'}
                        </button>
                      </div>

                      {typeMovements.length > 0 && (
                        <div className="pc-stock-history">
                          {typeMovements.map((m) => (
                            <div key={m.id} className="pc-stock-history-item">
                              <span className={`pc-stock-qty ${m.quantity >= 0 ? 'positive' : 'negative'}`}>
                                {m.quantity >= 0 ? '+' : ''}{m.quantity}
                              </span>
                              <span className="pc-stock-reason">{STOCK_MOVEMENT_REASON_LABELS[m.reason]}</span>
                              {m.note && <span className="pc-stock-note">{m.note}</span>}
                              <span className="pc-stock-date">{formatDate(m.created_at)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <aside className="detail-sidebar">
        {existing && form.types.length > 0 && (
          <section className="side-section">
            <h4>Summary</h4>
            <div className="stat-row">
              <span>Total in stock</span>
              <strong>{form.types.reduce((sum, t) => sum + (form.stockPools[t] ?? 0), 0)}</strong>
            </div>
          </section>
        )}
        <SidebarPhotoCard
          url={form.image || undefined}
          alt="Item preview"
          uploading={imageUploading}
          onFileSelect={handleImageFile}
          onRemove={() => set('image', '')}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleImageFile(f); }}
        />
      </aside>
    </div>

    {/* Stock movements above are what happened to the item; this is the
        conversation about it — supplier disputes, quality notes, why a
        pool was corrected. Comments only: a catalogue item has no status
        column, just an archived flag. */}
    <RecordActivity entityType="product" entityId={existing?.id} />
    </>
  );
};

export default CatalogItemFormPage;
